import { useGetProfileImage } from '@openeducation/oe-rn-providers';
import { styled, useDimension } from '@openeducation/oe-rn-themes';
import React, { useMemo } from 'react';

import { Card } from '../../atoms/card/card';
import { InfoItem } from '../../atoms/info-item/info-item';
import { TestId } from '../../constants/test-id';

export interface TeacherProfileProps {
  fullName: string;
  personId: number;
  displayName?: string | null;
  role: string;
  accountName?: string | null;
  facultyId?: string | null;
}

export const TeacherProfile = ({
  accountName,
  displayName,
  facultyId,
  fullName,
  personId,
  role,
}: TeacherProfileProps) => {
  const { base64Image } = useGetProfileImage({ size: 200, personId }, true);
  const { isMobile } = useDimension();
  const numColumns = isMobile ? 1 : 2;
  const flexBasis = `${94 / numColumns}%`;

  const profilePicture = useMemo(() => {
    if (!base64Image) {
      return;
    }

    return `data:image/jpg;base64,${base64Image}`;
  }, [base64Image]);

  return (
    <Card title="Profile Information" flex>
      <Container isMobile={isMobile} testID={TestId.PROFILE_CARD}>
        <ProfilePicture source={{ uri: profilePicture }} isMobile={isMobile} />
        <InnerContainer>
          <InfoItem title="Full Name" text={fullName} style={{ flexBasis }} />
          <InfoItem
            title="Display Name"
            text={displayName}
            style={{ flexBasis }}
          />
          <InfoItem title="Role" text={role} style={{ flexBasis }} />
          <InfoItem
            title="Account Name"
            text={accountName}
            style={{ flexBasis }}
          />
          <InfoItem title="Faculty ID" text={facultyId} style={{ flexBasis }} />
          <InfoItem style={{ flexBasis }} />
        </InnerContainer>
      </Container>
    </Card>
  );
};

interface ContainerProps {
  isMobile: boolean;
}

const Container = styled.View<ContainerProps>`
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
`;

const InnerContainer = styled.View`
  flex-basis: 70%;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px;
  margin-left: 16px;
  align-items: flex-start;
  justify-content: space-between;
`;

interface ProfilePictureProps {
  isMobile: boolean;
}

const ProfilePicture = styled.Image<ProfilePictureProps>`
  width: 160px;
  height: 160px;
  border-radius: 80px;
  background-color: ${({ theme }) => theme.color.backgroundsDarkGray};
  ${({ isMobile }) =>
    isMobile ? 'margin-bottom: 32px;' : 'margin-right: 32px;'}
`;
